import http from './http'
import { getMediaOrigin } from './config'

const LIST_PAGE_PARAMS = { page: 1, per_page: 999 }
let homeCasesCache = null
let homeCasesPromise = null
let caseCategoriesCache = null
let caseCategoriesPromise = null
const casesByCategoryCache = new Map()
const casesByCategoryPromise = new Map()
const caseDetailCache = new Map()
const caseDetailPromise = new Map()

function unwrapArray(payload) {
  if (payload == null) return []
  if (Array.isArray(payload)) return payload
  const d = payload.data
  if (Array.isArray(d)) return d
  if (d && typeof d === 'object' && Array.isArray(d.data)) return d.data
  return []
}

function resolveStorageUrl(path) {
  if (!path || typeof path !== 'string') return ''
  const t = path.trim()
  if (!t) return ''
  // 兼容：后端可能直接返回绝对地址（包含 /storage/uploads/...），前端期望显示成 /uploads/ 目录
  if (/^https?:\/\//i.test(t) || t.startsWith('//') || t.startsWith('data:')) {
    // 只要包含 /storage/uploads 就统一替换
    if (/\/storage\/uploads/i.test(t)) return t.replace(/\/storage\/uploads/ig, '/uploads')
    return t
  }
  const base = getMediaOrigin().replace(/\/$/, '')
  // 兼容：后端有时会把上传目录写成 storage/uploads/...，前端期望去掉 storage/
  if (t.startsWith('/storage/uploads') || t.startsWith('storage/uploads')) {
    const rest = t.replace(/^\/?storage\//, '') // uploads/...
    return `${base}/${rest.replace(/^\//, '')}`
  }
  if (t.startsWith('/storage/') || t.startsWith('storage/')) {
    const p = t.startsWith('/') ? t : `/${t}`
    return `${base}${p}`
  }
  if (t.startsWith('/uploads/') || t.startsWith('uploads/')) {
    const p = t.startsWith('/') ? t : `/${t}`
    return `${base}${p}`
  }
  return `${base}/storage/${t.replace(/^\//, '')}`
}

function resolveHtmlUrl(path) {
  if (!path || typeof path !== 'string') return path
  const t = path.trim()
  if (!t) return t
  if (/^https?:\/\//i.test(t) || t.startsWith('//') || t.startsWith('data:') || t.startsWith('#')) {
    if (/\/storage\/uploads/i.test(t)) return t.replace(/\/storage\/uploads/ig, '/uploads')
    return t
  }
  const base = getMediaOrigin().replace(/\/$/, '')
  if (t.startsWith('/storage/uploads') || t.startsWith('storage/uploads')) {
    const rest = t.replace(/^\/?storage\//, '') // uploads/...
    return `${base}/${rest.replace(/^\//, '')}`
  }
  return `${base}/${t.replace(/^\//, '')}`
}

function normalizeRichHtml(raw) {
  if (typeof raw !== 'string') return ''
  const html = raw.trim()
  if (!html) return ''
  return html.replace(/(src|href|data-href)=["']([^"']+)["']/gi, (_m, attr, url) => {
    const resolved = resolveHtmlUrl(url)
    return `${attr}="${resolved}"`
  })
}

function normalizeCaseCategory(row) {
  if (!row || typeof row !== 'object') return null
  const id =
    row.id ??
    row.anli_category_id ??
    row.category_id ??
    (row.anli_category && typeof row.anli_category === 'object'
      ? row.anli_category.id
      : undefined)
  if (id == null) return null
  const name =
    row.name ||
    row.title ||
    row.category_name ||
    (row.anli_category && (row.anli_category.name || row.anli_category.title)) ||
    '未命名'
  return {
    id: Number(id) || id,
    name,
    title: row.title || name
  }
}

function normalizeCase(row) {
  if (!row || typeof row !== 'object') return null
  const cover =
    row.cover_photo_path ||
    row.cover ||
    row.image ||
    row.pic ||
    row.image_url ||
    row.imageUrl ||
    ''
  const catId = row.anli_category_id ?? row.category_id ?? row.anli_categories_id
  const catName =
    row.category ||
    row.category_name ||
    row.type ||
    (row.anli_category && typeof row.anli_category === 'object'
      ? row.anli_category.name || row.anli_category.title
      : '') ||
    '综合案例'
  return {
    id: row.id ?? row.case_id ?? row.anli_id,
    title: row.title || row.name || row.case_name || '案例',
    subtitle: row.subtitle || row.description || row.summary || '',
    category: catName,
    categoryId: catId != null ? catId : undefined,
    image: resolveStorageUrl(typeof cover === 'string' ? cover : ''),
    link: row.link || row.url || row.href || ''
  }
}

function normalizeCaseDetail(row) {
  const base = normalizeCase(row)
  if (!base) return null
  const intro =
    (typeof row.intro === 'string' && row.intro) ||
    (typeof row.summary === 'string' && row.summary) ||
    (typeof base.subtitle === 'string' ? base.subtitle : '') ||
    ''
  const raw = row.body || row.content_html || row.html_content || row.content || row.detail || ''
  const s = typeof raw === 'string' ? raw.trim() : ''
  const isHtml = s.length > 0 && /<[a-z][\s\S]*>/i.test(s)
  return {
    ...base,
    intro,
    contentHtml: isHtml ? normalizeRichHtml(s) : '',
    contentPlain: !isHtml && s ? s : ''
  }
}

export const FALLBACK_CASES = [
  { id: 1, title: '清华大学图书馆', subtitle: '高校图书馆 · 智慧终端升级', category: '高校图书馆', categoryId: 1, image: '', intro: '重点高校场景下的智慧化升级案例。', contentPlain: '' },
  { id: 2, title: '雄安图书馆', subtitle: '城市级新馆 · 综合服务', category: '公共图书馆', categoryId: 2, image: '', intro: '城市级公共文化地标项目案例。', contentPlain: '' },
  { id: 3, title: '深圳图书馆北馆', subtitle: '智能立体书库', category: '智能立体书库', categoryId: 3, image: '', intro: '地下大型智能书库与分拣系统建设案例。', contentPlain: '' },
  { id: 4, title: '首都图书馆大兴机场分馆', subtitle: '交通枢纽 · 文化驿站', category: '城市书房', categoryId: 4, image: '', intro: '机场阅读空间智慧化服务案例。', contentPlain: '' },
  { id: 5, title: '云南经贸职业技术学院', subtitle: '高职馆 · 智慧化改造', category: '中小学案例', categoryId: 5, image: '', intro: '职业院校图书馆改造案例。', contentPlain: '' },
  { id: 6, title: '榆林市图书馆', subtitle: '区域中心馆', category: '公共图书馆', categoryId: 2, image: '', intro: '区域级图书馆整体智能化案例。', contentPlain: '' }
]

/** 与 FALLBACK_CASES 中 categoryId 对应 */
export const FALLBACK_CASE_CATEGORIES = [
  { id: 1, name: '高校图书馆' },
  { id: 2, name: '公共图书馆' },
  { id: 3, name: '智能立体书库' },
  { id: 4, name: '城市书房' },
  { id: 5, name: '中小学案例' }
]

function unwrapAnliList(raw) {
  let rows = []
  if (Array.isArray(raw)) {
    rows = raw
  } else if (raw && typeof raw === 'object') {
    if (Array.isArray(raw.anlis)) rows = raw.anlis
    else if (Array.isArray(raw.data)) rows = raw.data
    else if (Array.isArray(raw.items)) rows = raw.items
    else if (Array.isArray(raw.list)) rows = raw.list
    else if (Array.isArray(raw.cases)) rows = raw.cases
    else rows = [raw]
  }
  return rows.map(normalizeCase).filter(Boolean)
}

/**
 * 案例分类列表（Laravel：GET /anli_categories，通常挂在 /api/v1 下）
 */
export async function fetchCaseCategories() {
  if (Array.isArray(caseCategoriesCache)) return caseCategoriesCache.map((c) => ({ ...c }))
  if (caseCategoriesPromise) return caseCategoriesPromise.then((list) => list.map((c) => ({ ...c })))

  caseCategoriesPromise = (async () => {
    try {
      const { data } = await http.get('/api/v1/anli_categories')
      const raw = data?.data !== undefined ? data.data : data
      const list = unwrapArray({ data: raw }).map(normalizeCaseCategory).filter(Boolean)
      if (list.length) return list
    } catch (e) {
      console.warn('[cases] 分类接口失败，使用本地分类', e)
    }
    return FALLBACK_CASE_CATEGORIES.map((c) => ({ ...c }))
  })()
    .then((list) => {
      caseCategoriesCache = list
      return list
    })
    .finally(() => {
      caseCategoriesPromise = null
    })

  return caseCategoriesPromise.then((list) => list.map((c) => ({ ...c })))
}

/**
 * 按左侧分类 id 拉取右侧列表（Laravel：GET /anlis）
 * 同时传 anli_category_id、category_id（与左侧分类的 id 相同），兼容不同控制器参数名。
 * 若整请求失败，再分别单独尝试，避免部分网关丢弃重复键。
 */
export async function fetchCasesByCategory(categoryId) {
  const id = categoryId
  if (id == null || id === '') return []
  const key = String(id)
  if (casesByCategoryCache.has(key)) {
    return casesByCategoryCache.get(key).map((c) => ({ ...c }))
  }
  if (casesByCategoryPromise.has(key)) {
    return casesByCategoryPromise.get(key).then((list) => list.map((c) => ({ ...c })))
  }

  const load = async (params) => {
    const { data } = await http.get('/api/v1/anlis', { params: { ...params, ...LIST_PAGE_PARAMS } })
    const raw = data?.data !== undefined ? data.data : data
    return unwrapAnliList(raw)
  }

  const req = (async () => {
    try {
      return await load({ anli_category_id: id, category_id: id })
    } catch (e) {
      console.warn('[cases] anlis 组合参数失败，尝试单独 anli_category_id', id, e)
    }

    try {
      return await load({ anli_category_id: id })
    } catch (e) {
      console.warn('[cases] anlis?anli_category_id 失败，尝试 category_id', id, e)
    }

    try {
      return await load({ category_id: id })
    } catch (e) {
      console.warn('[cases] anlis?category_id 失败', id, e)
    }

    const cat = FALLBACK_CASE_CATEGORIES.find((c) => String(c.id) === String(id))
    if (!cat) return []
    return FALLBACK_CASES.filter((c) => String(c.categoryId) === String(id)).map((c) => ({ ...c }))
  })()
    .then((list) => {
      casesByCategoryCache.set(key, list)
      return list
    })
    .finally(() => {
      casesByCategoryPromise.delete(key)
    })

  casesByCategoryPromise.set(key, req)
  return req.then((list) => list.map((c) => ({ ...c })))
}

/** 全部案例（首页区块等）：GET /api/v1/anlis */
export async function fetchCases() {
  if (Array.isArray(homeCasesCache)) return homeCasesCache.map((c) => ({ ...c }))
  if (homeCasesPromise) return homeCasesPromise.then((list) => list.map((c) => ({ ...c })))

  homeCasesPromise = (async () => {
    try {
      const { data } = await http.get('/api/v1/anlis', { params: LIST_PAGE_PARAMS })
      const raw = data?.data !== undefined ? data.data : data
      const list = unwrapAnliList(raw)
      if (list.length) return list
    } catch (e) {
      console.warn('[cases] 全量列表接口失败，使用本地数据', e)
    }
    return FALLBACK_CASES.map((c) => ({ ...c }))
  })()
    .then((list) => {
      homeCasesCache = list
      return list
    })
    .finally(() => {
      homeCasesPromise = null
    })

  return homeCasesPromise.then((list) => list.map((c) => ({ ...c })))
}

export async function fetchCaseDetail(caseId) {
  const id = caseId
  if (id == null || id === '') return null
  const key = String(id)
  if (caseDetailCache.has(key)) {
    const cached = caseDetailCache.get(key)
    return cached ? { ...cached } : null
  }
  if (caseDetailPromise.has(key)) {
    return caseDetailPromise.get(key).then((d) => (d ? { ...d } : null))
  }

  const tryPaths = [
    `/api/v1/anlis/item/${id}`,
    `/api/v1/anlis/${id}`,
    `/api/v1/anli/${id}`,
    `/api/v1/cases/${id}`,
    `/api/v1/case/${id}`
  ]
  const req = (async () => {
    for (const url of tryPaths) {
      try {
        const { data } = await http.get(url)
        const raw = data?.data !== undefined ? data.data : data
        if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
          const d = normalizeCaseDetail(raw)
          if (d) return d
        }
      } catch (e) {
        if (url === tryPaths[tryPaths.length - 1]) {
          console.warn('[cases] 详情接口失败', id, e)
        }
      }
    }

    const local = FALLBACK_CASES.find((c) => String(c.id) === String(id))
    return local ? { ...local } : null
  })()
    .then((detail) => {
      caseDetailCache.set(key, detail)
      return detail
    })
    .finally(() => {
      caseDetailPromise.delete(key)
    })

  caseDetailPromise.set(key, req)
  return req.then((d) => (d ? { ...d } : null))
}
